
import { Box, Button, Checkbox, Dialog, DialogContent, FormControlLabel, IconButton, makeStyles, MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
// import NotInterestedIcon from "@material-ui/icons/NotInterestedOutlined";
import { ReactComponent as ImageArrowDown } from '../../images/contents/map/mappoint/arrow-down.svg';
import clsx from "clsx";
import { forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { DATA_HAS_BEEN_CHANGED_ERROR } from "../../utils/LinksAPI";
import RoleControl from "../RoleControl";
import { SUBJ_THRESHOLD_SETTING_LIST, VERB_UPDATE } from "../../utils/AccessControl";
import { useThresholdList } from "../../store/ThresholdSettingState";
import { deepCopy, zeroPadding } from "../../utils/FPEUtils";
import Color from "color";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	hide: {
		display: 'none',
	},
	flexColumn: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	flexVariable: {
		overflow: 'hidden',
		flexGrow: 1,
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
			whiteSpace: 'nowrap',
		},
		// '&:nth-child(2)': {
		// 	position: 'sticky',
		// 	left: '34px',
		// 	zIndex: 2,
		// 	// fontWeight: 'bold',
		// 	whiteSpace: 'nowrap',
		// },
		height: '30px',
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+1)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	checkCell: {
		verticalAlign: 'middle',
		transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	checkIcon: {
		width: 24,
		height: 24,
		// margin: '0 auto',
		'& .MuiSvgIcon-root': {
			color: 'rgb(2, 136, 209)',
		}
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
	alignRight: {
		textAlign: 'right',
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formTitle: {
		fontWeight: 'bold',
	},
	formBody: {
		marginTop: '8px',
		marginLeft: '16px',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		// '&:not(:first-child)': {
		// 	marginTop: '20px',
		// },
		'& .required': {
			color: '#f44336',
		},
		'& input[type=color]': {
			width: '50px',
			height: '30px',
		},
	},
	formInputLonLat: {
		maxWidth: '12em',
	},
	formInputRoot: {
		display: 'flex',
		columnGap: '8px',
		alignItems: 'center',
		width: '256px',
	},
	formInputContents: {
		display: 'flex',
		columnGap: '8px',
		alignItems: 'center',
	},
	formInputSwitch: {
		marginLeft: 0,
		'& .MuiFormControlLabel-label': {
			fontSize: '14px',
		}
	},
	formInputNumber: {
		width: '96px',
		'& .MuiInputBase-input': {
			paddingTop: '5px',
			paddingBottom: '5px',
			textAlign: 'right',
		},
		// '& .MuiFormHelperText-root': {
		// 	whiteSpace: 'nowrap',
		// },
	},
	formInputSelect: {
		width: '96px',
		'& .MuiInputBase-input': {
			paddingTop: '5px',
			paddingBottom: '5px',
		}
	},
	formInputWx: {
		display: 'flex',
		columnGap: '8px',
		alignItems: 'center',
		minWidth: '68px',
		marginLeft: 0,
		marginRight: 0,
		'& .MuiFormControlLabel-label': {
			fontSize: '14px',
		}
	},
	formInputWxDetail: {
		marginLeft: '16px',
		display: 'flex',
		flexWrap: 'wrap',
		columnGap: '12px',
		position: 'relative',
	},
	formInputWxAnnotationBox: {
		width: '100%',
	},
	formInputWxAnnotation: {
		position: 'absolute',
		right: 0,
		fontSize: '12px',
	},
	formInputColor: {
		width: '50px',
		height: '30px',
		border: 'none',
		backgroundColor: 'transparent',
	},
	formRoot: {
		// fontSize: '75%',
		'& .required': {
			color: '#f44336',
		},
		'& dl:not(:first-child)': {
			marginTop: '20px',
		},
		'& dt': {
			// marginBottom: '5px',
			width: '80px',
		},
		'& #iconId-checkbox': {
			display: 'flex',
			minWidth: '10rem',
		},
	},
	formHorizontally: {
		display: 'flex',
		flexDirection: 'row',
		// alignItems: 'baseline',
		alignItems: 'center',
	},
	formSpacer: {
		'& > *:not(:first-of-type)': { 
			marginLeft: '20px'
		},
	},
	formHeading: {
		marginRight: '10px',
	},
	menuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuItemCheckbox: {
		padding: 4,
	},
	menuItemText: {
		marginLeft: 4,
	},
	importButton: {
		padding: 0,
	},
	verticalCenter: {
		display: 'flex',
		alignItems: 'center',
	},
	arrowIcon: {
		height: 16,
		width: 16,
	},
	editButton: {
		// padding: '4px 15px',
		textTransform: 'none',
		// lineHeight: 1,
		color: 'rgba(0, 0, 0, 0.87)',
		'&.Mui-selected': {
			// backgroundColor: 'rgba(0, 0, 0, 0.3)',
			backgroundColor: 'rgba(2, 136, 209, 0.5)',
			'&:hover': {
				// backgroundColor: 'rgba(0, 0, 0, 0.33)',
				backgroundColor: 'rgba(2, 136, 209, 0.53)',
			},
		},
	},
	threshold: {
		display: 'flex',
		flexDirection: 'row',
		columnGap: '8px',
	},
});

const formatSign = (sign) => {
	let ret;
	switch (sign) {
		case "ge":
			ret = "≥";
			break;
		case "le":
			ret = "≤";
			break;
		case "gt":
			ret = ">";
			break;
		case "lt":
			ret = "<";
			break;
		case "eq":
			ret = "=";
			break;
		case "ne":
			ret = "≠";
			break;
		default:
			ret = "";
			break;
	}
	return ret;
};

const CloudCoverTypeValue = ['FEW', 'SCT', 'BKN', 'OVC'];

const columnDef = [
	{
		name: 'Category',
		code: 'alertLevel',
		classes: ['capitalize'],
	},
	{
		name: 'Active',
		code: 'active',
		type: 'active',
		default: false,
	},
	{
		name: 'Wind',
		code: 'wind',
		flag: 'windActive',
		type: 'number',
		sign: 'ge',
		unit: 'kt',
		condition: {
			required: true,
			type: 'integer',
			min: 0,
			max: 99,
		},
		default: 0,
	},
	{
		name: 'Gust Wind',
		code: 'gustWind',
		flag: 'gustWindActive',
		type: 'number',
		sign: 'ge',
		unit: 'kt',
		condition: {
			required: true,
			type: 'integer',
			min: 0,
			max: 99,
		},
		default: 0,
	},
	{
		name: 'VIS',
		code: 'vis',
		flag: 'visActive',
		type: 'number',
		sign: 'le',
		unit: 'm',
		condition: {
			required: true,
			type: 'integer',
			min: 0,
			max: 9999,
		},
		default: 0,
	},
	{
		name: 'CIG',
		code: 'cig',
		flag: 'cigActive',
		type: 'number',
		sign: 'le',
		unit: 'x100ft',
		format: (data) => {
			if (data.cig !== undefined) {
				return zeroPadding(data.cig, 3);
			}
		},
		condition: {
			required: true,
			type: 'integer',
			min: 0,
			max: 999,
		},
		default: 0,
	},
	{
		name: 'Cloud',
		code: 'cloudBaseAltitude',
		flag: 'cloudActive',
		type: 'number',
		sign: 'le',
		unit: 'x100ft',
		format: (data) => {
			if (data.cloudCoverType !== undefined && data.cloudBaseAltitude !== undefined) {
				return `${data.cloudCoverType}${zeroPadding(data.cloudBaseAltitude, 3)}`;
			}
		},
		condition: {
			required: true,
			type: 'integer',
			min: 0,
			max: 999,
		},
		default: 0,
	},
	{
		name: 'Temp1',
		code: 'temp1',
		flag: 'temp1Active',
		type: 'number',
		sign: 'ge',
		unit: '°C',
		condition: {
			required: true,
			type: 'integer',
			min: -99,
			max: 99,
		},
		default: 0,
	},
	{
		name: 'Temp2',
		code: 'temp2',
		flag: 'temp2Active',
		type: 'number',
		sign: 'le',
		unit: '°C',
		condition: {
			required: true,
			type: 'integer',
			min: -99,
			max: 99,
		},
		default: 0,
	},
	{
		name: 'WX',
		code: 'wx',
		classes: ['limit'],
		format(data) {
			return wxDef
				.flatMap((w) => {
					if (data[w.code]) {
						return w.elements.filter((v) => data[v.code]).map((v) => v.name);
					} else {
						return [];
					}
				})
				.join(',');
		},
		title: true,
	},
];

const wxDef = [
	{
		code: 'qualifierFlag',
		name: 'Qualifier',
		elements: [
			{
				name: 'LGT',
				nameFull: 'LGT(-)',
				code: 'lgtM',
				annotation: true,
			},
			{
				name: 'MOD',
				nameFull: 'MOD()',
				code: 'mod',
				annotation: true,
			},
			{
				name: 'HVY',
				nameFull: 'HVY(+)',
				code: 'hvyP',
				annotation: true,
			},
			{
				name: 'VC',
				code: 'vc',
			},
			{
				name: 'MI',
				code: 'mi',
			},
			{
				name: 'BC',
				code: 'bc',
			},
			{
				name: 'PR',
				code: 'pr',
			},
			{
				name: 'DR',
				code: 'dr',
			},
			{
				name: 'BL',
				code: 'bl',
			},
			{
				name: 'SH',
				code: 'sh',
			},
			{
				name: 'TS',
				code: 'ts',
			},
			{
				name: 'FZ',
				code: 'fz',
			},
		],
	},
	{
		code: 'obscurationFlag',
		name: 'Obscuration',
		elements: [
			{
				name: 'BR',
				code: 'br',
			},
			{
				name: 'FG',
				code: 'fg',
			},
			{
				name: 'FU',
				code: 'fu',
			},
			{
				name: 'VA',
				code: 'va',
			},
			{
				name: 'DU',
				code: 'du',
			},
			{
				name: 'SA',
				code: 'sa',
			},
			{
				name: 'HZ',
				code: 'hz',
			},
		],
	},
	{
		code: 'precipitationFlag',
		name: 'Precipitation',
		elements: [
			{
				name: 'DZ',
				code: 'dz',
			},
			{
				name: 'RA',
				code: 'ra',
			},
			{
				name: 'SN',
				code: 'sn',
			},
			{
				name: 'SG',
				code: 'sg',
			},
			{
				name: 'PL',
				code: 'pl',
			},
			{
				name: 'GR',
				code: 'gr',
			},
			{
				name: 'GS',
				code: 'gs',
			},
			{
				name: 'IC',
				code: 'ic',
			},
			{
				name: 'UP',
				code: 'up',
			},
		],
	},
	// {
	// 	code: 'otherFlag',
	// 	name: 'Other',
	// 	elements: [
	// 		{
	// 			name: 'PO',
	// 			code: 'po',
	// 		},
	// 		{
	// 			name: 'SQ',
	// 			code: 'sq',
	// 		},
	// 		{
	// 			name: 'FC',
	// 			code: 'fc',
	// 		},
	// 		{
	// 			name: 'SS',
	// 			code: 'ss',
	// 		},
	// 		{
	// 			name: 'DS',
	// 			code: 'ds',
	// 		},
	// 		{
	// 			name: 'PY',
	// 			code: 'py',
	// 		},
	// 	],
	// },
	// {
	// 	code: 'supplyFlag',
	// 	name: 'Supply',
	// 	elements: [
	// 		{
	// 			name: 'WS',
	// 			code: 'ws',
	// 		},
	// 		{
	// 			name: 'RE',
	// 			code: 're',
	// 		},
	// 	],
	// },
];

const importSampleData = {
	wind: 30,
	windActive: true,
	gustWind: 60,
	gustWindActive: true,
	vis: 1000,
	visActive: true,
	cig: 10,
	cigActive: true,
	cloudCoverType: 'BKN',
	cloudBaseAltitude: 8,
	cloudActive: true,
	temp1: 35,
	temp1Active: true,
	temp2: -2,
	temp2Active: true,
	qualifierFlag: true,
	lgtM: false,
	mod: false,
	hvyP: false,
	vc: false,
	mi: false,
	bc: false,
	pr: false,
	dr: false,
	bl: false,
	sh: false,
	ts: true,
	fz: false,
	obscurationFlag: true,
	br: false,
	fg: true,
	fu: false,
	va: true,
	du: false,
	sa: false,
	hz: false,
	precipitationFlag: true,
	dz: false,
	ra: false,
	sn: true,
	sg: false,
	pl: false,
	gr: false,
	gs: true,
	ic: false,
	up: false,
	// otherFlag: false,
	// po: false,
	// sq: false,
	// fc: false,
	// ss: false,
	// ds: false,
	// py: false,
	// supplyFlag: true,
	// ws: true,
	// re: false,
};

const staticInvalidData = {
	otherFlag: false,
	po: false,
	sq: false,
	fc: false,
	ss: false,
	ds: false,
	py: false,
	supplyFlag: false,
	ws: false,
	re: false,
};

const ThresholdListTableElementShow = memo(forwardRef(({inputThresholdRefs, col, alertId}, ref) => {
	const data = inputThresholdRefs[alertId] ?? {};
	const classes = useStyles();
	switch(col.code) {
		case 'active':
			return (
				<Box className={classes.formInputRoot}>
					{/* <Box>Active</Box> */}
					<Box className={clsx(classes.checkIcon)}>{ data.active ? <CheckIcon /> : <></> }</Box>
				</Box>
			);
		case 'wx':
			return (
				<Box className={classes.formInputRoot}>
					<Box>
						{ wxDef.map((wx) => (
							<dl key={`wx_key_${alertId}_${wx.code}`}>
								<dt>
									<Box className={classes.formInputWx}>
										<Box className={clsx(classes.checkIcon)}>{ data[wx.code] ? <CheckIcon /> : <></> }</Box>
										<Box>{ wx.name }</Box>
									</Box>
								</dt>
								<dd className={classes.formInputWxDetail}>
									{ wx.elements.map((e) => (
										<Box key={`wx_key_${alertId}_${wx.code}_${e.code}`} className={clsx(classes.formInputWx, e.annotation && classes.formInputWxAnnotationBox)}>
											<Box className={clsx(classes.checkIcon)}>{ data[e.code] ? <CheckIcon /> : <></> }</Box>
											<Box>{ e.nameFull ?? e.name }</Box>
											{ e.annotation &&
												<Box component="span" className={classes.formInputWxAnnotation}>
													*only precipitation & others
												</Box>
											}
										</Box>
									)) }
								</dd>
							</dl>
						)) }
					</Box>
				</Box>
			);
		default:
			return (
				<Box className={classes.formInputRoot}>
					<Box className={clsx(classes.checkIcon)}>{ data[col.flag] ? <CheckIcon /> : <></> }</Box>
					<Box>{ formatSign(col.sign) }</Box>
					<Box>{ col.format ? col.format(data) : data[col.code] }</Box>
				</Box>
			);
	}
}));

const ThresholdListTableElementEdit = memo(forwardRef(({col, alertId}, ref) => {
	const {t} = useTranslation();
	const { control, formState: { errors }, getValues, setValue, watch } = useFormContext();
	const isActive = watch(`${alertId}.active`);

	const classes = useStyles();

	const handleParentChange = useCallback((event) => {
		const [alertId, wxCode] = event.target.name.split('.');
		const wx = wxDef.find(wx => wx.code === wxCode);
		if (wx !== undefined) {
			for(const e of wx.elements) {
				setValue(`${alertId}.${e.code}`, event.target.value);
			}
		}
	},[setValue]);

	const handleElementChange = useCallback((event, wxCode) => {
		const [alertId] = event.target.name.split('.');
		if (event.target.value) {
			setValue(`${alertId}.${wxCode}`, true);
		} else {
			const wx = wxDef.find(wx => wx.code === wxCode);
			if (wx !== undefined) {
				if (wx.elements.every(e => !getValues(`${alertId}.${e.code}`))) {
					setValue(`${alertId}.${wxCode}`, false);
				}
			}
		}
	},[getValues, setValue]);

	switch(col.code) {
		case 'active':
			return (
				<Box className={classes.formInputRoot}>
					<Controller
						key={`${alertId}.${col.code}`}
						control={control}
						render={({ field: { name, onChange, value } }) => (
							<FormControlLabel
								control={
									<Switch
										checked={value}
										onChange={onChange}
										color="primary"
										name={name}
										size="small"
										inputProps={{ 'aria-label': 'primary checkbox' }}
									/>
								}
								// label="Active"
								// labelPlacement="start"
								className={clsx(classes.formInputSwitch)}
							/>
						)}
						name={`${alertId}.${col.code}`}
					/>
				</Box>
			);
		case 'wx':
			return (
				<Box className={classes.formInputRoot}>
					<Box>
						{ wxDef.map((wx) => (
							<dl key={`wx_key_edt_${alertId}_${wx.code}`}>
								<dt>
									<Box>
										<Controller
											key={`${alertId}.${wx.code}`}
											control={control}
											rules={{
												onChange: handleParentChange,
											}}
											render={({ field: { name, onChange, value } }) => (
												<FormControlLabel
													control={
														<Checkbox
															checked={value}
															name={name}
															style={{ padding: 0 }}
															disabled={!isActive}
															onChange={onChange}
														/>
													}
													label={ wx.name }
													className={classes.formInputWx}
												/>
											)}
											name={`${alertId}.${wx.code}`}
										/>
									</Box>
								</dt>
								<dd className={classes.formInputWxDetail}>
									{ wx.elements.map((e) => (
										<Box key={`wx_key_edt_${alertId}_${wx.code}_${e.code}`} className={clsx(classes.formInputWx, e.annotation && classes.formInputWxAnnotationBox)}>
											<Controller
												key={`${alertId}.${e.code}`}
												control={control}
												rules={{
													onChange: (event) => handleElementChange(event, wx.code)
												}}
												render={({ field: { name, onChange, value } }) => (
													<FormControlLabel
														control={
															<Checkbox
																checked={value}
																onChange={onChange}
																name={name}
																style={{ padding: 0 }}
																disabled={!isActive}
															/>
														}
														label={ e.nameFull ?? e.name }
														className={classes.formInputWx}
													/>
												)}
												name={`${alertId}.${e.code}`}
											/>
											{ e.annotation &&
												<Box component="span" className={classes.formInputWxAnnotation}>
													*only precipitation & others
												</Box>
											}
										</Box>
									)) }
								</dd>
							</dl>
						)) }
					</Box>
				</Box>
			);
		default:
			return (
				<Box className={classes.formInputRoot}>
					<Box>
						<Controller
							key={`${alertId}.${col.flag}`}
							control={control}
							render={({ field: { name, onChange, value } }) => (
								<Checkbox
									name={name}
									checked={value}
									style={{ padding: 0 }}
									disabled={!isActive}
									onChange={onChange}
								/>
							)}
							name={`${alertId}.${col.flag}`}
						/>
					</Box>
					<Box>
						{ col.code === 'cloudBaseAltitude' &&
							<Box className={classes.formInputContents} style={{ marginBottom: '8px' }}>
								<Box style={{ width: '0.5em' }}></Box>
								<Controller
									key={`${alertId}.${col.flag}`}
									control={control}
									render={({ field: { name, onChange, value } }) => (
										<TextField
											className={classes.formInputSelect}
											name={name}
											id={`${name}-select`}
											variant="outlined"
											select
											size="small"
											disabled={!isActive}
											InputLabelProps={{
												shrink: true,
											}}
											value={value}
											onChange={onChange}
										>
										{CloudCoverTypeValue.map((v) => (
											<MenuItem key={v} value={v}>
												<Box className={classes.verticalCenter}>
													{v !== 'OVC' && <Box className={classes.verticalCenter}>
														<ImageArrowDown className={classes.arrowIcon} />
													</Box>}
													<Box>{v}</Box>
												</Box>
											</MenuItem>
										))}
										</TextField>
									)}
									name={`${alertId}.cloudCoverType`}
								/>
							</Box>
						}
						<Box className={classes.formInputContents}>
							<Box>{ formatSign(col.sign) }</Box>
							<Box >
								<Controller
									key={`${alertId}.${col.code}`}
									control={control}
									rules={{
										required: col.condition.required ? t('validate_required') : false,
										min: {
											value: col.condition.min,
											message: t('validate_number_out_of_range', { min: col.condition.min, max: col.condition.max }),
										},
										max: {
											value: col.condition.max,
											message: t('validate_number_out_of_range', { min: col.condition.min, max: col.condition.max }),
										},
										pattern: {
											value: /^-?\d+$/,
											message: t('validate_pattern')
										},
									}}
									render={({ field: { name, onChange, value } }) => (
										<TextField
											className={classes.formInputNumber}
											name={name}
											id={name}
											variant="outlined"
											size="small"
											type="number"
											disabled={!isActive}
											InputLabelProps={{
												shrink: true,
											}}
											inputProps={{
												required: col.condition.required,
												step: 1,
												min: col.condition.min,
												max: col.condition.max,
											}}
											error={!!errors[alertId]?.[col.code]}
											helperText={errors[alertId]?.[col.code]?.message}
											value={value}
											onChange={(value) => {
												if (Number.isNaN(value.target.valueAsNumber)) {
													return onChange("");
												}
												return onChange(value.target.valueAsNumber)
											}}
										/>
									)}
									name={`${alertId}.${col.code}`}
								/>
							</Box>
						</Box>
					</Box>
				</Box>
			);
	}
}));

const ThresholdListTable = memo(forwardRef(({editMode, setWaiting, setError, tableRef}, ref) => {
	const {
		thresholdColorList,
		thresholdList,
		updateThresholdColorList,
		updateThresholdList,
		refreshThresholdList
	} = useThresholdList();
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const { reset, setValue } = useFormContext();

	useEffect(() => {
		return () => {
			refreshThresholdList();
		}
	}, [refreshThresholdList]);

	useEffect(() => {
		if (thresholdColorList.length > 0 && 'threshold' in thresholdList) {
			setError(false);
		} else {
			setError(true);
		}
	}, [thresholdColorList, thresholdList, setError]);

	useImperativeHandle(ref, () => ({
		updateColor: (updateData) => {
			setWaiting(true);
			updateThresholdColorList(updateData)
				.then(() => {
					// console.info("update comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === 409 || e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("update failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshThresholdList();
					} else {
						console.error("update error", e);
						showError(`${t('update_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		update: (updateData) => {
			setWaiting(true);
			updateThresholdList(updateData)
				.then(() => {
					// console.info("update comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === 409 || e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("update failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshThresholdList();
					} else {
						console.error("update error", e);
						showError(`${t('update_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		thresholdColorList,
		edit: () => {
			const threshold = deepCopy(inputThresholdRefs);
			editableThresholdColorList.forEach(thresholdColor => {
				const alertId = thresholdColor.alertId;
				if (!(alertId in threshold)) {
					threshold[alertId] = {};
				}
				columnDef.forEach(col => {
					if ('default' in col && !(col.code in threshold[alertId])) {
						threshold[alertId][col.code] = col.default;
					}
					if ('flag' in col && !(col.flag in threshold[alertId])) {
						threshold[alertId][col.flag] = false;
					}
					if (col.code === 'cloudBaseAltitude') {
						if (!('cloudCoverType' in threshold[alertId])) {
							threshold[alertId].cloudCoverType = 'FEW';
						}
					}
				});
				wxDef.forEach(col => {
					if (!(col.code in threshold[alertId])) {
						threshold[alertId][col.code] = false;
					}
					col.elements.forEach(e => {
						if (!(e.code in threshold[alertId])) {
							threshold[alertId][e.code] = false;
						}
					})
				})
			});
			reset(threshold);
		},
	}));

	const editableThresholdColorList = useMemo(() => thresholdColorList.slice(0, -1), [thresholdColorList]);

	const inputThresholdRefs = useMemo(() => {
		const m = thresholdList.threshold?.map((v) => [v.alertId, { ...v }]);
		if (m !== undefined) {
			return Object.fromEntries(m);
		} else {
			return {};
		}
	}, [thresholdList]);

	const handleImport = useCallback((alertId) => {
		Object.entries(importSampleData).forEach(([key, val], index, array) => {
			if (index + 1 === array.length) {
				setValue(`${alertId}.${key}`, val, { shouldValidate: true, shouldDirty: true });
			} else {
				setValue(`${alertId}.${key}`, val);
			}
		})
	}, [setValue]);

	return (
		<>
			<Box className={classes.flexColumn}>
				<Box className={classes.flexVariable}>
					<TableContainer className={classes.container} ref={tableRef}>
						<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
							<TableHead>
								<TableRow className={classes.tableRow}>
									<TableCell
										className={clsx(classes.headerCell, classes.tableCell)}
									>
										Element
									</TableCell>
									{editableThresholdColorList.map((thresholdColor) => (
										<TableCell
											key={`dlg_head_key_${thresholdColor.alertId}`}
											className={clsx(classes.headerCell, classes.tableCell)}
										>
											<Box style={{ display: 'flex', justifyContent: 'space-between' }}>
												<Box style={{ display: 'flex', alignItems: 'center', columnGap: '16px' }}>
													<Box style={{ width: '14px', height: '14px', border: '1px solid black', backgroundColor: thresholdColor.alertColor }}>
													</Box>
													{ thresholdColor.alertLevel }
												</Box>
												{ editMode &&
													<Button
														variant="contained"
														size="small"
														className={classes.importButton}
														onClick={() => handleImport(thresholdColor.alertId)}
													>
														{t('Import')}
													</Button>
												}
											</Box>
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{columnDef.slice(1).map((col) => {
									return (
										<TableRow hover role="checkbox" tabIndex={-1} key={`dlg_body_tr_key_${col.code}`}>
											<TableCell
												className={clsx(classes.fixedCell, classes.tableCell)}
											>
												{ col.nameFull ?? col.name }
												{ col.unit !== undefined ? ` [${col.unit}]` : '' }
											</TableCell>
											{editableThresholdColorList.map(thresholdColor => {
												return (
													<TableCell
														key={`tb_${col.id}_${thresholdColor.alertId}`}
														className={clsx(classes.tableCell)}
													>
														{ !editMode ?
															<ThresholdListTableElementShow
																inputThresholdRefs={inputThresholdRefs}
																col={col}
																alertId={thresholdColor.alertId}
															/>
														:
															<ThresholdListTableElementEdit
																col={col}
																alertId={thresholdColor.alertId}
															/>
														}
													</TableCell>
												)
											})}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Box>
		</>
	);
}));

const ThresholdColorDialog = memo(forwardRef(({thresholdColorList}, ref) => {
	const classes = useStyles();

	const { control } = useFormContext();

	return (
		<Box className={classes.formBody}>
			<ul className={classes.formRoot} style={{ marginTop: '20px' }}>
				{thresholdColorList.map((thresholdColor, i) => (
					<li key={`dlg_color_${thresholdColor.alertId}`}>
						<dl className={classes.formHorizontally}>
							<dt>{ thresholdColor.alertLevel }</dt>
							<dd>
								<Controller
									key={`${thresholdColor.alertId}`}
									control={control}
									render={({ field: { name, onChange, value } }) => (
										<input
											type="color"
											className={classes.formInputColor}
											name={name}
											id={name}
											value={value}
											onChange={onChange}
										/>
									)}
									name={`${thresholdColor.alertId}.alertColor`}
								/>
							</dd>
						</dl>
					</li>
				))}
			</ul>
			<div></div>
		</Box>
	);
}));

function ThresholdList() {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const [open, setOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const [error, setError] = useState(false);

	const methods = useForm({
		mode: 'onChange',
	});
	const { isDirty, isValid } = methods.formState;
	const getValues = methods.getValues;

	const classes = useStyles();
	const {t} = useTranslation();

	// Threshold Color
	const handleThresholdColorEdit = () => {
		const m = childRef.current.thresholdColorList.map(thresholdColor => {
			return [thresholdColor.alertId, {
				alertColor: new Color(thresholdColor.alertColor).hex().toLowerCase(),
			}];
		});
		const thresholdColorList = Object.fromEntries(m);
		methods.reset(thresholdColorList);
		setOpen(true);
	}

	const handleClose = () => {
		setOpen(false);
	}

	const handleColorSave = (event) => {
		event.preventDefault();
		// event.target.blur();

		const d = getValues();
		const data = {
			thresholdColor: childRef.current.thresholdColorList.map(thresholdColor => ({
				alertId: thresholdColor.alertId,
				alertColor: new Color(d[thresholdColor.alertId].alertColor).rgb().string().replaceAll(' ', ''),
				updatedTime: thresholdColor.updatedTime,
			}))
		}

		childRef.current.updateColor(data);

		setOpen(false);
	}

	// Threshold
	const handleThresholdEdit = () => {
		setEditMode(true);
		childRef.current.edit();
	}

	const handleCancel = () => {
		setEditMode(false);
	}

	const handleSave = () => {
		// event.preventDefault();
		// event.target.blur();

		const d = getValues();
		const data = {
			threshold: Object.entries(d).map(([alertId, v]) => ({
				alertId: Number.parseInt(alertId),
				...v,
				...staticInvalidData
			})),
		}

		childRef.current.update(data);

		setEditMode(false);
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{waiting && <Loading backdrop={true} />}
			<Suspense fallback={<Loading />}>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '10px 50px 10px 10px' }}>
					{ !editMode ?
						<>
							<RoleControl subject={SUBJ_THRESHOLD_SETTING_LIST} verb={VERB_UPDATE}>
								<Button
									variant="outlined"
									size="small"
									disabled={error}
									onClick={handleThresholdColorEdit}
								>
									{t('Color Edit')}
								</Button>
							</RoleControl>
							<RoleControl subject={SUBJ_THRESHOLD_SETTING_LIST} verb={VERB_UPDATE}>
								<Button
									variant="outlined"
									size="small"
									disabled={error}
									onClick={handleThresholdEdit}
								>
									{t('Threshold Edit')}
								</Button>
							</RoleControl>
						</>
					:
						<>
							<Button
								variant="outlined"
								size="small"
								className={classes.btn}
								style={{ marginRight: 20 }}
								onClick={handleCancel}
							>
								{t('Cancel')}
							</Button>
							<RoleControl subject={SUBJ_THRESHOLD_SETTING_LIST} verb={VERB_UPDATE}>
								<Button
									variant="outlined"
									size="small"
									disabled={!(isDirty && isValid)}
									className={classes.btn}
									onClick={handleSave}
								>
									{t('save')}
								</Button>
							</RoleControl>
						</>
					}
				</Box>
				<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
					<FormProvider {...methods}>
						<ThresholdListTable ref={childRef} editMode={editMode} setWaiting={setWaiting} setError={setError} tableRef={tableRef}/>
					</FormProvider>
				</Box>
				<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
					<DialogContent>
						<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
							<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
								<Box style={{ marginLeft: 'auto'}}>
									<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
										<CloseIcon />
									</IconButton>
								</Box>
								<Box>
									<Button
										variant="outlined"
										size="small"
										className={classes.btn}
										disabled={!(isDirty && isValid)}
										onClick={handleColorSave}
									>
										{t('save')}
									</Button>
								</Box>
							</Box>
							<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
								<FormProvider {...methods}>
									<ThresholdColorDialog thresholdColorList={childRef.current.thresholdColorList} />
								</FormProvider>
							</Box>
						</Box>
					</DialogContent>
				</Dialog>
			</Suspense>
		</Box>
	)
}

export default ThresholdList;
